/**
 * Reusable PIX payment block: QR Code image + copy-and-paste code + bank
 * instructions. Shared by the public charge page (`PayChargePage`) and the
 * authenticated reservation payment flow (`ReservationDetailPage`).
 *
 * Field naming follows the public charge contract (`payload` + `qrCodeBase64`).
 * Surfaces that expose the Pix as `encodedImage` (e.g. the reservation
 * payment-details endpoint) should map `encodedImage -> qrCodeBase64` before
 * rendering.
 */

import { useState } from 'react'
import { Button, ButtonVariant, Caption } from '@meiuca/ui-components'
import { Check, Copy } from 'lucide-react'

export interface PixPaymentProps {
  /** Pix copy-and-paste payload (BR Code / EMV string). */
  payload: string
  /** Base64-encoded PNG of the Pix QR Code (without the data URI prefix). */
  qrCodeBase64: string
  /** Optional ISO expiration shown as a hint; purely informational. */
  expiresAt?: string | null
  /** Overrides the default bank instructions caption. */
  instructions?: string
}

const DEFAULT_INSTRUCTIONS =
  'Abra o app do seu banco, escolha PIX → Pix Copia e Cola, cole o código e confirme.'

export function PixPayment({ payload, qrCodeBase64, instructions }: PixPaymentProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payload)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Older browsers / insecure contexts — fall back to manual select.
      const ta = document.createElement('textarea')
      ta.value = payload
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } finally {
        document.body.removeChild(ta)
      }
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <img
          src={`data:image/png;base64,${qrCodeBase64}`}
          alt="QR Code PIX"
          className="w-56 h-56 rounded-lg border border-gray-200"
          width={224}
          height={224}
        />
      </div>

      <Button variant={ButtonVariant.PRIMARY} onClick={handleCopy} className="w-full justify-center">
        {copied ? (
          <>
            <Check size={18} className="mr-2" />
            Código copiado!
          </>
        ) : (
          <>
            <Copy size={18} className="mr-2" />
            Copiar código PIX
          </>
        )}
      </Button>

      <Caption className="text-text-tertiary text-center">
        {instructions ?? DEFAULT_INSTRUCTIONS}
      </Caption>
    </div>
  )
}
